import { CompanyId } from './../companies/CompanyResponse';
import { email } from './../../../core/utils/form/validation/validators';
import { CompactUserResponse, UserId, VeryCompactUserResponse } from '@business/entities/account';
import { CompactInvoiceResponse } from '@business/entities/TransactionInvoice';
import { BaseResponse } from '../common';
import { CompactContactResponse, ContactId } from '../contact';
import { CompactOrgResponse, OrgId } from '../organization';
import { BoardId } from './../project/BoardResponse';
import { ProjectPriorityResponse } from './ProjectPriorityResponse';
import { ProjectSectionResponse, CompactProjectSectionResponse } from './ProjectSectionResponse';
import { StageId } from './ProjectStageResponse';
import { LineItem } from './SubscriptionResponse';
import { ProjectTemplateId } from './TemplateResponse';
import { ProposalId } from '../proposal';

export enum ProjectStatus {
  pending = 1,
  active = 2,
  completed = 3,
  cancelled = 4,
  overdue = 5,
  archived = 6,
  all = 'all',
}

export type ProjectId = string;

export interface CompactProjectResponse extends BaseResponse, ProjectMulticurrencyResponse {
  id: ProjectId;
  title: string;
  description: string;
  amount: number;
  hours: number | null;
  organization: CompactOrgResponse;
  user: CompactUserResponse;
  accountManager?: CompactUserResponse;
  salesRep?: CompactUserResponse;
  contact?: CompactContactResponse;
  startDate: number;
  endDate?: number;
  cancelledAt: number | null;
  completedAt: number | null;
  markCompletedAt: number | null;
  total: number;
  balance: number;
  menuOrder: number;
  priority: ProjectPriorityResponse | null;
  template: string | null;
  templateId: ProjectTemplateId;
  boardId: BoardId;
  stageId: BoardId;
  boardName: BoardId;
  status: ProjectStatus;
  isWeekActivity: boolean;
  dueStatus: string;
  invoiceLink: string;
  billingEmail: string;
  lineItems: LineItem[];
  invoice?: CompactInvoiceResponse;
  isCanceled: boolean;
  canceledAt: number | null;
  isArchived: boolean;
  currency: string;
  onBoard: boolean;
  trackingTime: boolean;
  totalTimeTracked: number;
}

export interface VeryCompactProjectResponse {
  id: ProjectId;
  title: string;
  description: string;
  boardName: string;
  orgName: string;
  sections: CompactProjectSectionResponse[];
}

export interface ProjectResponse extends CompactProjectResponse {
  sections: ProjectSectionResponse[];
  histories: ProjectHistoryResponse[];
  notes: string;
  isShared: boolean;
  users: ProjectUserResponse[];
  proposals?: ProjectProposalResponse[];
  aiData?: {
    summary?: string;
    lastUpdate: number;
  };
}
export interface ProjectProposalResponse {
  id: ProposalId;
  orgId: OrgId;
  title: string;
}

export type ProjectRequest = Pick<CompactProjectResponse, 'title' | 'description' | 'hours'> & {
  accountManager?: UserId;
  salesRep?: UserId;
  contact: ContactId;
  dueDate?: number;
  deposit?: number;
};

export type ConvertProjectRequest = {
  transactionId: number;
  title: string;
  description?: string;
  board: BoardId;
  stage: StageId;
  salesRep?: UserId;
  contact: ContactId;
  hours?: number;
  billingEmail: string;
  invoiceDate?: number;
  dueDate?: number;
  billingAddress: string;
};

export enum ProjectHistoryType {
  endDate = 'endDate',
}

export interface EndDateChange {
  from: number;
  to: number;
}

export interface ProjectHistoryResponse extends BaseResponse {
  id: string;
  type: ProjectHistoryType;
  change: EndDateChange;
  user: CompactUserResponse;
}

export type ProjectUpdateRequest = Pick<CompactProjectResponse, 'title' | 'description' | 'hours'> & {
  board?: BoardId;
  stage?: StageId;
};

export interface BoardProjectResponse extends ProjectMulticurrencyResponse {
  id: ProjectId;
  title: string;
  amount: number;
  hours: number | null;
  organizationId: OrgId;
  organizationName: string;
  user: VeryCompactUserResponse;
  accountManager?: VeryCompactUserResponse;
  startDate: number;
  endDate?: number;
  cancelledAt: number | null;
  completedAt: number | null;
  total: number;
  balance: number;
  menuOrder: number;
  priority: ProjectPriorityResponse | null;
  boardId: BoardId;
  boardName: string;
  stageId: BoardId;
  status: ProjectStatus;
  isWeekActivity: boolean;
  dueStatus: string;
  stageName: string;
  progress: number;
  isArchived: boolean;
  currency: string;
}

export interface ProjectMulticurrencyResponse {
  amountDefault: number;
  balanceDefault: number;
}

export enum ProjectPermission {
  view = 'view',
  ownManage = 'ownManage',
  allManage = 'allManage',
}

export const ProjectPermissions = [ProjectPermission.view, ProjectPermission.ownManage, ProjectPermission.allManage];

export interface ProjectPermissionRequest {
  email: string;
  permissionType: ProjectPermission;
}

export interface ShareProjectResponse {
  email: string;
  appId: string;
  companyId: CompanyId;
  projectId: ProjectId;
}

export interface ProjectUserResponse {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  name: string;
  isClient: boolean;
  userType: ProjectUserType;
}

export enum ProjectUserType {
  user = 'user',
  projectUser = 'projectUser',
}

export interface ProjectLinkResponse {
  url: string;
}
